import { useState } from "react";
import { ArrowRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { useLiveMarkets, type LiveMarket } from "@/hooks/useLiveMarkets";
import { Skeleton } from "@/components/ui/skeleton";
import MiniSparkline from "./MiniSparkline";

/* ─── Expandable Sidebar Card ─── */
const SidebarMarketCard = ({ market }: { market: LiveMarket }) => {
  const [expanded, setExpanded] = useState(false);

  // Handle missing API history gracefully
  const hasHistory = market.history && market.history.length > 1;
  const safeProb = Number(market.probability) || 50;
  const now = Math.floor(Date.now() / 1000);
  const displayHistory = hasHistory ? market.history : [{ t: now - 86400 * 7, v: safeProb }, { t: now, v: safeProb }];

  const momentum = hasHistory ? market.history[market.history.length - 1].v - market.history[0].v : 0;
  const probColor = momentum > 2 ? "text-green-400" : momentum < -2 ? "text-red-400" : "text-foreground";

  const handleClick = () => setExpanded(!expanded);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setExpanded(!expanded);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="block px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer outline-none focus-visible:bg-muted/50"
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <h4 className="text-xs font-bold text-foreground leading-snug line-clamp-2 flex-1">{market.question}</h4>
        <span className={`text-lg font-extrabold leading-none shrink-0 ${probColor}`}>{market.probability}%</span>
      </div>
      {market.topOutcome && !["yes", "no"].includes(market.topOutcome.toLowerCase()) && (
        <div className="text-[10px] mb-1 line-clamp-1">
          <span className="text-muted-foreground italic">Leading: </span>
          <span className="font-medium text-primary">{market.topOutcome}</span>
        </div>
      )}

      <div className={`mb-1.5 transition-all duration-300 ${expanded ? "h-16" : "h-6"}`}>
        <MiniSparkline data={displayHistory} positive={momentum >= 0} height={expanded ? 64 : 24} showTooltip={expanded} />
      </div>

      <div className="flex items-center justify-between mt-1">
        <div className="flex items-center gap-1">
          <ExternalLink className="w-2.5 h-2.5 text-muted-foreground/60" />
          <span className="text-[10px] text-muted-foreground">{market.platform}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground">{market.volumeTotal} vol</span>
          <span className="text-[9px] text-primary font-medium">{expanded ? "Collapse" : "Details"}</span>
        </div>
      </div>

      {expanded && (
        <div className="mt-2 space-y-2">
          {market.siblings && market.siblings.length >= 1 && (
            <div className="space-y-1 border-t border-border pt-2">
              <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide">Other outcomes</span>
              {market.siblings.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-[11px]">
                  <span className="text-muted-foreground truncate flex-1 mr-2">{s.label}</span>
                  <span className="font-bold text-foreground shrink-0">{s.probability}%</span>
                </div>
              ))}
            </div>
          )}
          {market.sourceUrl && (
            <a
              href={market.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-[10px] text-primary font-semibold hover:underline underline-offset-2"
            >
              View on {market.platform} <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
        </div>
      )}
    </div>
  );
};

const MarketSidebar = ({ count = 4 }: { count?: number }) => {
  const { data, isLoading } = useLiveMarkets(count);
  const markets = data?.markets ?? [];

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-foreground">Market Pulse</h3>
          <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            Live
          </span>
        </div>
        <Link
          to="/markets"
          className="text-xs text-primary font-semibold hover:underline underline-offset-4 inline-flex items-center gap-1"
        >
          More <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="divide-y divide-border">
        {isLoading
          ? Array.from({ length: count }).map((_, i) => (
              <div key={i} className="px-4 py-3 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-6 w-1/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))
          : markets.map((m) => <SidebarMarketCard key={m.id} market={m} />)}
      </div>
    </div>
  );
};

export default MarketSidebar;
